import { formatCurrency, formatUSD } from "@vaulzo/core/constants";
import { formatUnits, holdingsByBroker, stockGainINRPercent } from "@vaulzo/core/domain/stocks";
import { toneClass } from "./ui";

const usd2 = (v) => formatUSD(v, { decimals: 2, suffix: "" });
const arrow = (n) => (n >= 0 ? "↑" : "↓");

/**
 * Portfolio view: current value, ₹ put in and return, then holdings and
 * cash per broker. Everything here is worked out from the entries; ₹
 * figures use the same ₹/$ rate as the portfolio total.
 */
export default function PortfolioTab({ asset, entryCount }) {
  const totalUSD = asset.total_value_usd || 0;
  const totalINR = asset.current_value || 0;
  const rate = totalUSD > 0 ? totalINR / totalUSD : 0;
  const invested = asset.invested_inr || 0;
  const gain = stockGainINRPercent(asset);
  const ret = totalINR - invested;
  const groups = holdingsByBroker(asset);
  const inr = (usd) => formatCurrency(usd * rate);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-x-7 gap-y-5 border-b border-pf-line bg-white px-7 pb-6 pt-7">
        <div className="col-span-full flex flex-col gap-1">
          <div className="text-[12px] text-pf-faint">Current value</div>
          <div className="flex flex-wrap items-baseline gap-3">
            <div className="text-[38px] font-semibold leading-none tracking-[-0.03em]">{usd2(totalUSD)}</div>
            <div className="text-[16px] font-medium text-pf-soft">{formatCurrency(totalINR)}</div>
          </div>
        </div>
        <Stat label="Put in">{invested > 0 ? formatCurrency(invested) : "—"}</Stat>
        <Stat label="Return" className={gain != null ? toneClass(ret) : ""}>
          {gain != null
            ? `${ret >= 0 ? "+" : "−"}${formatCurrency(Math.abs(ret))} · ${arrow(gain)} ${Math.abs(gain).toFixed(2)}%`
            : "—"}
        </Stat>
        <Stat label="Rate used">{rate ? `₹${rate.toFixed(2)}/$` : "—"}</Stat>
      </div>

      <div className="flex flex-col gap-[18px] px-7 pb-8 pt-[22px]">
        <div className="flex items-baseline justify-between">
          <div className="text-[13px] font-semibold">Holdings</div>
          <div className="text-[12px] text-pf-faint">
            Derived from {entryCount} entr{entryCount === 1 ? "y" : "ies"}
          </div>
        </div>

        {groups.length === 0 && (
          <div className="rounded-xl border border-pf-line bg-white p-8 text-center text-[13px] text-pf-faint">
            Nothing held yet. Add a conversion in Activity to get started.
          </div>
        )}

        {groups.map((g) => (
          <section key={g.broker} className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between px-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-pf-muted">{g.broker}</span>
              <span className="text-[13px] font-semibold">
                {usd2(g.usd)} <span className="font-medium text-pf-faint">· {inr(g.usd)}</span>
              </span>
            </div>
            <div className="overflow-hidden rounded-xl border border-pf-line bg-white">
              {g.holdings.map((h) => {
                const value = h.units * (h.last_price || 0);
                const change =
                  h.buy_price_usd > 0 && h.last_price > 0
                    ? ((h.last_price - h.buy_price_usd) / h.buy_price_usd) * 100
                    : null;
                return (
                  <Row
                    key={h.symbol}
                    title={h.symbol}
                    desc={h.name}
                    detail={[`${formatUnits(h.units)} unit${h.units === 1 ? "" : "s"}`, `avg ${usd2(h.buy_price_usd || 0)}`]}
                    value={usd2(value)}
                    inr={inr(value)}
                    change={change}
                  />
                );
              })}
              {(g.cash > 0.005 || g.holdings.length === 0) && (
                <Row title="Cash" desc="Uninvested" detail={["USD"]} value={usd2(g.cash)} inr={inr(g.cash)} />
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, className = "", children }) {
  return (
    <div className="flex flex-col gap-[3px]">
      <div className="text-[12px] text-pf-faint">{label}</div>
      <div className={`text-[15px] font-semibold ${className}`}>{children}</div>
    </div>
  );
}

function Row({ title, desc, detail, value, inr, change = null }) {
  return (
    <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_auto] items-center gap-x-5 gap-y-1 border-b border-pf-divider px-[18px] py-[13px] transition-colors last:border-b-0 hover:bg-pf-hover">
      <div className="flex min-w-0 flex-col gap-[3px]">
        <div className="truncate text-[14px] font-semibold">{title}</div>
        {desc && <div className="truncate text-[12px] text-pf-faint">{desc}</div>}
      </div>
      <div className="flex flex-col gap-[3px] text-[12px] text-pf-muted">
        {detail.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="flex flex-col gap-[3px] text-right">
        <div className="text-[14px] font-semibold">{value}</div>
        <div className="whitespace-nowrap text-[12px] text-pf-faint">
          {inr}
          {change != null && (
            <span className={`ml-[6px] font-semibold ${toneClass(change)}`}>
              {arrow(change)} {Math.abs(change).toFixed(2)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
